<script setup lang="ts">
// Standalone, prop-driven mic pulse (round-23, 2026-08-31 — "the mic button on Home should
// visibly pulse with live audio strength", the header MicButton's own pulse was there but too
// subtle to read at normal speaking distance). Pure presentation: no stt.ts/settings.ts import,
// no click handling — MicButton.vue (header, small) and Home.vue (bigger, standalone) both wrap
// this in their own <button> and feed it `level`/`state`, so the pulse rendering itself exists
// in exactly one place instead of two copies of the same scale-transform math.
//
// `level` is kmb-stt's raw rms (0..~0.3 in practice — see DebugStt.vue's meter, which scales the
// same value by ×300 to fill a bar; normal speaking volume sits well under the top of that
// range, closer to 0.02-0.15).
//
// Round-24 (2026-08-31): moved from two hard-edged expanding rings to a filled, blurred glow.
//
// Round-26 (2026-08-31, user feedback on round-24's glow: "must be a CIRCLE, and pulse MUCH
// more"): two changes.
//  1. This is now always a filled round BUTTON (a solid-colour circle with the icon on it,
//     border for definition in the off state), not just an icon with a halo behind it — reads as
//     a real round voice-assistant affordance in every state, not only while live. Home wraps it
//     in a plain hit-target <button> (no background of its own — MicPulse now owns the entire
//     visible circle) sized ~2x the previous halo; MicButton's header instance is unchanged
//     (same small size, same wrapping button).
//  2. The level→visual mapping is sqrt-boosted (`normalizedLevel` below), not linear: kmb-stt's
//     rms rarely gets near its own ~0.3 ceiling during normal speech, so a linear 0..0.5 clamp
//     (round-24) barely moved the needle for a normal speaking voice — only shouting/clipping
//     read as dramatic. sqrt(level / 0.3) makes ordinary speech volume swell the halo clearly
//     visibly while still capping cleanly at 1 for loud input. The core circle itself now also
//     breathes a little (not just the icon glyph), and the two glow layers extend and brighten
//     much further than round-24 — see glowStyle() below for the actual scale factors.
// No glow at all for 'off'/'broken' (matches round-24 — a steady filled circle carries those
// states, no pulsing). Driven purely by CSS transitions on each incoming `level` prop change
// (stt.ts receives a 'level' WS message several times a second) — no rAF loop needed.
import { computed } from 'vue'
import { mdiMicrophone, mdiMicrophoneOff } from '@mdi/js'

const props = withDefaults(
  defineProps<{
    level: number
    state: 'off' | 'broken' | 'live'
    /** Overall box size in px (core circle + halo headroom). Header uses ~20-24, Home's
     *  standalone instance goes much bigger (~2x its old halo). Everything below scales off
     *  this single prop so the header instance stays proportionally subtle automatically. */
    size?: number
  }>(),
  { size: 20 },
)

// sqrt-boosted 0..1: makes ordinary speaking volume (well under kmb-stt's own ~0.3 rms ceiling)
// produce a clearly visible swell instead of a barely-there one. Only 'live' pulses at all.
const normalizedLevel = computed(() => {
  if (props.state !== 'live') return 0
  return Math.min(Math.sqrt(props.level / 0.3), 1)
})

// The core circle itself breathes a little (not just the glow around it) — up to +14% at full
// level — plus the icon glyph scales a touch more on top of that for a bit of extra life.
const coreScale = computed(() => 1 + normalizedLevel.value * 0.14)
const iconScale = computed(() => 1 + normalizedLevel.value * 0.22)

function glowStyle(kind: 'inner' | 'outer') {
  const isOuter = kind === 'outer'
  const blur = props.size * (isOuter ? 0.22 : 0.1)
  if (props.state !== 'live') {
    return { transform: 'scale(0)', opacity: 0, filter: `blur(${blur}px)` }
  }
  const n = normalizedLevel.value
  // Round-26: both glow layers are full-box circles (see template's `inset-0`) scaled up by
  // `transform`, so these scale factors are multiples of the `size` prop itself (the core circle
  // is 0.72*size) — a scale of 1.0 already roughly matches the core's own footprint. Much wider
  // swing than round-24: outer halo goes from just past the core at rest to ~2.3x `size` (well
  // over 3x the core's own diameter) at full level; inner glow tracks tighter (~1.8x `size`) but
  // still very clearly.
  const restScale = isOuter ? 1.05 : 0.7
  const levelBoost = isOuter ? 1.3 : 1.1
  const scale = restScale + n * levelBoost
  const baseOpacity = isOuter ? 0.16 : 0.32
  const levelOpacity = isOuter ? 0.55 : 0.55
  const opacity = Math.min(baseOpacity + n * levelOpacity, isOuter ? 0.6 : 0.85)
  return {
    transform: `scale(${scale})`,
    opacity,
    filter: `blur(${blur}px)`,
  }
}

const coreColorClass = computed(() => ({
  'bg-neutral-800 border-2 border-neutral-600': props.state === 'off',
  'bg-red-600 border-2 border-red-400/50': props.state === 'broken',
  'bg-emerald-500 border-2 border-emerald-300/50': props.state === 'live',
}))
const iconColorClass = computed(() => ({
  'text-neutral-300': props.state === 'off',
  'text-white': props.state !== 'off',
}))
const glowColorClass = computed(() => (props.state === 'broken' ? 'bg-red-500' : 'bg-emerald-400'))
</script>

<template>
  <div class="relative flex items-center justify-center shrink-0" :style="{ width: `${size}px`, height: `${size}px` }">
    <!-- Halo layers sit behind the core circle, sized/blurred relative to the whole box so they
         can extend well past it. -->
    <span
      class="absolute inset-0 rounded-full pointer-events-none transition-[transform,opacity] duration-200 ease-out"
      :class="glowColorClass"
      :style="glowStyle('outer')"
    />
    <span
      class="absolute inset-0 rounded-full pointer-events-none transition-[transform,opacity] duration-150 ease-out"
      :class="glowColorClass"
      :style="glowStyle('inner')"
    />
    <!-- The core is the actual round "button" — always a filled circle so this reads as a
         voice-assistant affordance in every state, not only while live. -->
    <div
      class="relative rounded-full flex items-center justify-center transition-transform duration-150 ease-out"
      :class="coreColorClass"
      :style="{ width: `${size * 0.72}px`, height: `${size * 0.72}px`, transform: `scale(${coreScale})` }"
    >
      <svg
        viewBox="0 0 24 24"
        class="transition-transform duration-100 ease-out"
        :class="iconColorClass"
        :style="{ width: `${size * 0.36}px`, height: `${size * 0.36}px`, transform: `scale(${iconScale})` }"
        fill="currentColor"
      >
        <path :d="state === 'off' ? mdiMicrophoneOff : mdiMicrophone" />
      </svg>
    </div>
  </div>
</template>
